import { getDbSafe } from './firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';

/**
 * SCALE SIGNALS
 *
 * Three measurements that answer questions the codebase can't: whether a given
 * limit, price or window is sized for the app that exists or for a much busier
 * one that doesn't. Each block maps to a decision that was explicitly deferred
 * pending real numbers.
 *
 *   communities → is the 500/1000 gold creation price suppressing supply, or
 *                 filtering out dead groups like it's supposed to?
 *   discover    → how deep is a typical swipe pool once exclusions are applied?
 *                 A 10/30 gold price only makes sense against a pool that can
 *                 absorb it.
 *   signups     → does the 30-day "New Yappers This Month" window contain
 *                 enough people to fill a section?
 *
 * Read-only and computed on demand from the moderator dashboard. The users array
 * is passed in because the dashboard has already fetched it — no second read.
 */

// A community counts as alive if it has a handful of members AND someone has
// posted recently. Either alone is misleading: a 200-member ghost town looks
// healthy by member count, and a brand-new 2-person group looks healthy by
// recency.
const ALIVE_MIN_MEMBERS = 5;
const ALIVE_ACTIVITY_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface CommunitySignals {
    tribesTotal: number;
    forumsTotal: number;
    aliveCount: number;
    dormantCount: number;
    alivePercent: number;
    medianMembers: number;
    createdLast30d: number;
    emptyCount: number; // zero or one member — never got off the ground
}

export interface DiscoverSignals {
    discoverableTotal: number;
    adultPool: number;
    minorPool: number;
    medianFriends: number;
    /** Bracket pool minus the median user's friends and themselves. */
    estimatedMedianPool: number;
    /** Gold to swipe through that whole pool once, at the classic rate. */
    goldToExhaustPool: number;
    optedOutCount: number; // showInDiscover === false
}

export interface SignupSignals {
    last7d: number;
    last30d: number;
    last90d: number;
    /** How many of the last 30 days' signups the New Yappers section can show. */
    newYappersEligible: number;
    /** Days back you'd have to look to reach 20 eligible profiles. */
    daysToFillSection: number | null;
}

export interface ScaleSignals {
    communities: CommunitySignals;
    discover: DiscoverSignals;
    signups: SignupSignals;
    computedAt: number;
}

function toMillis(value: any): number {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (typeof value === 'number') return value;
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
}

function median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
        ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
        : sorted[mid];
}

// Mirrors the discovery filters: opted out, hidden by moderation, or banned
// accounts are not swipeable and must not inflate the pool estimate.
function isDiscoverable(u: any): boolean {
    if (u.showInDiscover === false) return false;
    if (u.accountHidden === true) return false;
    if (u.status === 'banned') return false;
    return true;
}

function friendCountOf(u: any): number {
    if (typeof u.friendsCount === 'number') return u.friendsCount;
    if (Array.isArray(u.friends)) return u.friends.length;
    return 0;
}

export const ScaleSignalsService = {
    /**
     * @param users Already-fetched user documents (moderator dashboard has these).
     */
    async compute(users: any[]): Promise<ScaleSignals> {
        const db = await getDbSafe();
        const now = Date.now();

        // ── Communities ───────────────────────────────────────────────────────
        const [tribesSnap, forumsSnap] = await Promise.all([
            getDocs(collection(db, 'tribes')),
            getDocs(collection(db, 'forums')),
        ]);

        const groups = [
            ...tribesSnap.docs.map(d => d.data() as any),
            ...forumsSnap.docs.map(d => d.data() as any),
        ].filter(g => g.status !== 'Archived');

        const memberCounts = groups.map(g => g.memberCount ?? 0);
        const aliveCount = groups.filter(g => {
            const members = g.memberCount ?? 0;
            const lastActivity = toMillis(g.lastActivityAt);
            const recentlyActive = lastActivity > 0 && (now - lastActivity) < ALIVE_ACTIVITY_DAYS * MS_PER_DAY;
            return members >= ALIVE_MIN_MEMBERS && recentlyActive;
        }).length;

        const communities: CommunitySignals = {
            tribesTotal: tribesSnap.size,
            forumsTotal: forumsSnap.size,
            aliveCount,
            dormantCount: groups.length - aliveCount,
            alivePercent: groups.length > 0 ? Math.round((aliveCount / groups.length) * 100) : 0,
            medianMembers: median(memberCounts),
            createdLast30d: groups.filter(g => {
                const created = toMillis(g.createdAt);
                return created > 0 && (now - created) < 30 * MS_PER_DAY;
            }).length,
            emptyCount: groups.filter(g => (g.memberCount ?? 0) <= 1).length,
        };

        // ── Discover pool depth ───────────────────────────────────────────────
        const discoverable = users.filter(isDiscoverable);
        const adultPool = discoverable.filter(u => u.ageGroup === 'adult').length;
        const minorPool = discoverable.filter(u => u.ageGroup !== 'adult').length;

        // The median user sits in whichever bracket is larger — use that as the
        // representative pool rather than averaging two unrelated populations.
        const representativeBracket = Math.max(adultPool, minorPool);
        const medianFriends = median(discoverable.map(friendCountOf));
        const estimatedMedianPool = Math.max(0, representativeBracket - medianFriends - 1);

        const discover: DiscoverSignals = {
            discoverableTotal: discoverable.length,
            adultPool,
            minorPool,
            medianFriends,
            estimatedMedianPool,
            goldToExhaustPool: estimatedMedianPool * 10, // SWIPE_COSTS.classic
            optedOutCount: users.filter(u => u.showInDiscover === false).length,
        };

        // ── Signups ───────────────────────────────────────────────────────────
        const signupAges = users
            .map(u => toMillis(u.createdAt))
            .filter(ms => ms > 0)
            .map(ms => (now - ms) / MS_PER_DAY);

        const withinDays = (days: number) => signupAges.filter(age => age <= days).length;

        // Same filters the New Yappers section applies, so this is what the
        // section can actually show — not just raw signups.
        const eligibleAges = users
            .filter(isDiscoverable)
            .map(u => toMillis(u.createdAt))
            .filter(ms => ms > 0)
            .map(ms => (now - ms) / MS_PER_DAY)
            .sort((a, b) => a - b);

        // How far back to look to fill a 20-card section.
        const daysToFillSection = eligibleAges.length >= 20
            ? Math.ceil(eligibleAges[19])
            : null;

        const signups: SignupSignals = {
            last7d: withinDays(7),
            last30d: withinDays(30),
            last90d: withinDays(90),
            newYappersEligible: eligibleAges.filter(age => age <= 30).length,
            daysToFillSection,
        };

        return { communities, discover, signups, computedAt: now };
    },
};
